import { useEffect, useRef, useState } from 'react';
import { Button, Callout, Flex, IconButton, Text, TextField } from '@radix-ui/themes';
import { KeyRound, SendHorizonal, X } from 'lucide-react';
import type { LinkState } from '@mac-remote/protocol';
import { getTelegramWebApp } from '../lib/telegram';

/**
 * The console's input line, and the one place a password is allowed to be typed.
 *
 * Deliberately not the project composer. Two reasons, both of which would bite:
 *
 *  - **Autocorrect.** The composer's textarea leaves iOS to its defaults, which is right
 *    for Russian prose aimed at Codex and wrong for a shell: the keyboard capitalises
 *    the first word (`Sudo`) and rewrites `--` into an en dash. Every field here turns
 *    all of that off.
 *  - **Persistence.** The composer records what it sends to `localStorage` — history for
 *    the snippet bar, plus a draft it restores after a reconnect. A sudo password must
 *    reach neither, so it does not travel through the same path as a command: the
 *    password field owns its own state, clears it on send, and tells nobody.
 *
 * One line, not three. A shell command is a line by nature, and Enter on a phone
 * keyboard should send it rather than open a second one.
 */
export function ConsoleInput({
  link,
  asking,
  value,
  onValueChange,
  onRun,
  onPassword,
  onAskPassword,
  onDismissPassword,
}: {
  link: LinkState;
  /** The Mac looks like it is waiting for a password right now. */
  asking: boolean;
  value: string;
  onValueChange: (text: string) => void;
  onRun: (text: string) => void;
  /** Send it followed by CR, and forget it. */
  onPassword: (secret: string) => void;
  onAskPassword: () => void;
  onDismissPassword: () => void;
}) {
  const online = link === 'online';
  const [secret, setSecret] = useState('');
  const [sent, setSent] = useState(false);
  const secretRef = useRef<HTMLInputElement>(null);

  // Nothing typed as a password outlives the prompt it was typed for — not in state,
  // not in a ref. Closing the field is what clears it.
  useEffect(() => {
    if (!asking) setSecret('');
  }, [asking]);

  useEffect(() => {
    if (asking) secretRef.current?.focus();
  }, [asking]);

  // "Отправлено" is the only feedback there can be: echo is off, so the terminal shows
  // nothing at all when a password lands, and without this the tap looks ignored.
  useEffect(() => {
    if (!sent) return;
    const id = window.setTimeout(() => setSent(false), 2500);
    return () => window.clearTimeout(id);
  }, [sent]);

  const run = () => {
    if (!online || !value.trim()) return;
    onRun(value);
    onValueChange('');
    getTelegramWebApp()?.HapticFeedback?.impactOccurred('light');
  };

  const sendSecret = () => {
    if (!online || !secret) return;
    onPassword(secret);
    setSecret('');
    setSent(true);
    onDismissPassword();
    getTelegramWebApp()?.HapticFeedback?.impactOccurred('medium');
  };

  return (
    <Flex direction="column" gap="2" px="2" pb="2" pt="1" style={{ background: 'var(--app-secondary-bg)' }}>
      {!online && (
        <Callout.Root color="amber" size="1">
          <Callout.Text>
            {link === 'stale' ? 'Мак не отвечает' : 'Мак офлайн'}. Ввод отключён — команда ушла бы
            вслепую.
          </Callout.Text>
        </Callout.Root>
      )}

      {sent && !asking && (
        <Text size="1" style={{ color: 'var(--jade-11)' }}>
          Пароль отправлен. Эха у него нет — смотри, что ответил мак.
        </Text>
      )}

      {asking ? (
        <>
          <Flex align="center" justify="between" gap="2">
            <Text size="1" weight="bold" style={{ color: 'var(--amber-11)' }}>
              Мак спрашивает пароль
            </Text>
            <IconButton size="1" variant="ghost" color="gray" title="Не сейчас" onClick={onDismissPassword}>
              <X size={14} />
            </IconButton>
          </Flex>
          <Flex gap="2">
            <TextField.Root
              ref={secretRef}
              type="password"
              placeholder="Пароль от мака"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  sendSecret();
                }
              }}
              size="3"
              // Every one of these matters for a password field on a phone: no
              // suggestion strip, no capitalised first character, no keychain offering
              // to remember it, and no spellcheck underlining it.
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              name="mac-remote-secret"
              style={{ flex: 1, fontSize: 16 }}
            />
            <Button size="3" disabled={!online || !secret} onClick={sendSecret}>
              <SendHorizonal size={15} />
            </Button>
          </Flex>
          {/* Precise on purpose: "уходит в то, что спрашивает", not "уходит в sudo". Тот
              же ключ открывает это поле руками, и если в тот момент никто пароля не ждёт,
              строка станет обычной командой — что видно на экране. Приложение при этом не
              сохраняет её ни при каком раскладе, и вот это обещание твёрдое. */}
          <Text size="1" style={{ color: 'var(--app-hint)' }}>
            Уходит в то, что сейчас спрашивает. Приложение не сохраняет его — ни в истории,
            ни в черновике.
          </Text>
        </>
      ) : (
        <Flex gap="2">
          <TextField.Root
            placeholder="Команда"
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                run();
              }
            }}
            size="3"
            // The shell equivalent of the list above. `Sudo softwareupdate` and an
            // autocorrected `--flag` are both silent failures, so none of it is left on.
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            style={{ flex: 1, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 16 }}
          />
          {/* The way in when the prompt was not recognised — a password can be asked for
              by anything, not just sudo, and the field must be reachable regardless. */}
          <Button size="3" variant="soft" color="amber" disabled={!online} title="Ввести пароль" onClick={onAskPassword}>
            <KeyRound size={15} />
          </Button>
          <Button size="3" disabled={!online || !value.trim()} onClick={run}>
            <SendHorizonal size={15} />
          </Button>
        </Flex>
      )}
    </Flex>
  );
}
